import React, { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

import * as UUID from '@/lib/uuid';
import { sendMessage, toggleDataEvents } from '@/redux/app/actions/event';
import { fetchTicket } from '@/redux/app/actions/ticket';
import { canViewTicketSelector, ticketContactDataSelector, ticketS3DirectPostSelector } from '@/redux/app/selectors/ticket';
import { NavigationPaneRouteParamsContext } from '@/lib/navigation';
import { S3FileUploader } from '@/lib/file-uploader';

import { AppSecretsContext } from '../../secrets/app-secrets';
import { UserInfoContext } from '../../user/user-info';
import { OnboardingInfoContext } from '../../onboarding/onboarding-info';
import { OnboardingContextHOC } from '../../onboarding/OnboardingContextHOC';

import { EmptyTicket } from '../EmptyTicket';
import { TicketChannel } from './TicketChannel';
import { TicketEmailModal } from './TicketEmailModal';
import { TicketHeader } from './TicketHeader';
import { TicketEvents } from './TicketEvents';
import { TicketChatbox, TicketChatboxProps } from './TicketChatbox';
import { NotesPanel } from './NotesPanel';
import { SurveyPanel } from './SurveyPanel';
import { ZoomedInMediaModal } from './ZoomedInMediaModal';
import { DragAndDropOverlay } from './DragAndDropOverlay';
import { OnboardingTicketView } from '../../onboarding/OnboardingTicketView';
import { TicketFileUploaderContext } from './ticket-file-uploader.context';
import { DropzoneContext, DropzoneListener } from '../../shared/dropzone';

const useDocumentTitleForTicket = (ticket: string) => {
  const contactData = useSelector(_.partial(ticketContactDataSelector, ticket));
  useEffect(() => {
    const name = contactData && (contactData.display_name ?? contactData.identifier);
    const cb = () => { document.title = name || 'Messages'; };
    cb();
    window.addEventListener('focus', cb);
    return () => window.removeEventListener('focus', cb);
  }, [contactData]);
};

export const TicketView = OnboardingContextHOC(() => {
  const [showModal, setShowModal] = useState(false);
  const { rememberToken } = useContext(AppSecretsContext);
  const { subdomain, ticket } = useContext(NavigationPaneRouteParamsContext);
  const { isRenderOnboardingTicketView } = useContext(OnboardingInfoContext);
  const { admin, canManage } = useContext(UserInfoContext);

  useDocumentTitleForTicket(ticket);

  const dispatch = useDispatch();
  const handleMessageSubmit = useCallback<TicketChatboxProps['handleSubmit']>((event) => {
    const { elements } = event.target;
    const message = elements.namedItem('message').value.trim();
    const media = elements.namedItem('media_url')?.value?.trim?.();
    if (!message && !media) return;

    const scheduledFor = elements.namedItem('scheduled_for').value.trim();
    const cancelOn = elements.namedItem('cancel_on')?.value?.trim?.();
    const mediaType = media && elements.namedItem('media_type').value.trim();
    const mediaDimensions = media && elements.namedItem('media_dimensions').value.trim();
    const temporaryId = UUID.generate();

    dispatch(sendMessage({
      subdomain,
      ticketUuid: ticket,
      message,
      media,
      mediaType,
      mediaDimensions,
      scheduledFor,
      cancelOn,
      temporaryId,
    }));
    sessionStorage.removeItem(`ticket:${ticket}`);
  }, [ticket, subdomain, dispatch]);

  const notesPanelRef = useRef<NotesPanel>();
  const handleNotesClick = useCallback(() => {
    notesPanelRef.current.open();
  }, [notesPanelRef]);

  const handleToggleDataEvents = () => {
    dispatch(toggleDataEvents(ticket));
  };

  useEffect(() => {
    dispatch(fetchTicket(subdomain, ticket));
  }, [subdomain, ticket, dispatch]);

  const canViewTicket = useSelector(_.partial(canViewTicketSelector, ticket))
    ?? (admin || canManage);

  const { url: s3Url, formdata: s3FormData, default_file_type: defaultFileType } =
    useSelector(_.partial(ticketS3DirectPostSelector, ticket)) ?? {};
  const { inputRef, addListener, removeListener, isDragActive } = useContext(DropzoneContext);
  const fileUploader = useMemo(
    () => s3Url && new S3FileUploader(s3Url, s3FormData, defaultFileType, inputRef),
    [s3FormData, inputRef],
  );
  useEffect(() => {
    if (!fileUploader) return undefined;

    const dropListener: DropzoneListener<File> = (event) => {
      fileUploader.triggerUpload(event.detail.acceptedFiles);
    };
    addListener(dropListener);

    return () => removeListener(dropListener);
  }, [fileUploader]);

  if (isRenderOnboardingTicketView) return <OnboardingTicketView />;
  if (!ticket || !canViewTicket) return <EmptyTicket />;

  return (
    <TicketFileUploaderContext.Provider value={fileUploader}>
      <div className="ticket-view-container ui-column-3" data-no-turbolinks="true">
        <TicketChannel ticket={ticket} style={{ display: 'none' }} />
        <TicketEmailModal
          showModal={showModal}
          setShowModal={setShowModal}
          subdomain={subdomain}
          ticket={ticket}
        />
        <TicketHeader ticket={ticket} handleNotesClick={handleNotesClick} />
        <NotesPanel
          ref={notesPanelRef}
          ticket={ticket}
          setShowModal={setShowModal}
          handleToggleDataEvents={handleToggleDataEvents}
        />
        <SurveyPanel />
        <TicketEvents ticket={ticket} />
        <TicketChatbox
          ticket={ticket}
          handleSubmit={handleMessageSubmit}
          rememberToken={rememberToken}
        />
        <ZoomedInMediaModal />
      </div>
      <DragAndDropOverlay visible={isDragActive} />
    </TicketFileUploaderContext.Provider>
  );
});
